import { Link, useRouterState } from "@tanstack/react-router";
import {
  Home,
  BookOpen,
  Sparkles,
  CalendarDays,
  User,
  Users,
  GraduationCap,
  LineChart,
  Wand2,
  Heart,
  BarChart3,
  Compass,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useAccountType, useRoleT } from "@/hooks/use-account-type";

type NavItem = { to: string; key: string; icon: typeof Home; label: string };

const STUDENT: NavItem[] = [
  { to: "/", key: "home", icon: Home, label: "Home" },
  { to: "/studio", key: "studio", icon: BookOpen, label: "Studio" },
  { to: "/ai", key: "ai", icon: Sparkles, label: "AI" },
  { to: "/planner", key: "planner", icon: CalendarDays, label: "Planner" },
  { to: "/explore", key: "explore", icon: Compass, label: "Esplora" },
];

const TEACHER: NavItem[] = [
  { to: "/teacher", key: "teacherHome", icon: GraduationCap, label: "Classi" },
  { to: "/teaching-studio", key: "teachingStudio", icon: Wand2, label: "Contenuti" },
  { to: "/teacher-results", key: "teacherResults", icon: LineChart, label: "Risultati" },
  { to: "/teacher-hub", key: "teacherHub", icon: Users, label: "Docenti" },
  { to: "/profile", key: "profile", icon: User, label: "Profilo" },
];

const PARENT: NavItem[] = [
  { to: "/family", key: "family", icon: Heart, label: "Famiglia" },
  { to: "/stats", key: "stats", icon: BarChart3, label: "Andamento" },
  { to: "/community", key: "community", icon: Users, label: "Community" },
  { to: "/profile", key: "profile", icon: User, label: "Profilo" },
];

export function BottomNav() {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const account = useAccountType();
  const rt = useRoleT();
  const items = account === "teacher" ? TEACHER : account === "parent" ? PARENT : STUDENT;
  return (
    <nav
      aria-label={t("nav.home")}
      className="fixed bottom-0 left-0 right-0 z-40 pb-[max(env(safe-area-inset-bottom),0.5rem)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]"
    >
      <div className="mx-auto max-w-md sm:max-w-2xl lg:max-w-4xl px-4 pb-3 pt-2">
        <div className="glass flex items-center justify-around rounded-3xl px-2 py-1.5 shadow-[var(--shadow-glass)]">
          {items.map((item) => {
            const { to, key, icon: Icon } = item;
            const active = to === "/" ? pathname === "/" : pathname.startsWith(to);
            const label = rt(`nav.${key}`, { defaultValue: item.label });
            return (
              <Link
                key={to}
                to={to}
                aria-label={label}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "group flex min-h-11 min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-2xl px-1 py-1.5 text-[9px] font-medium transition-all",
                  active
                    ? "bg-primary text-primary-foreground shadow-[var(--shadow-soft)]"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <Icon
                  className={cn("h-5 w-5 transition-transform", active && "scale-110")}
                  strokeWidth={active ? 2.4 : 1.8}
                />
                <span className="max-w-full truncate">{label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
